import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Button } from '@mdvip/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@mdvip/shared/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@mdvip/shared/components/ui/table';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { useTranslation } from 'react-i18next';
import { AccountCell } from '@/components/payments/AccountCell';
import type { PaymentTransaction } from '@/hooks/payments/transactions/transactionsApi';

export type ReceiptAction = 'email' | 'pdf';

interface TransactionRowProps {
  transaction: PaymentTransaction;
  /** Optional so the row can still be rendered in isolation, with both menu items inert. */
  onGenerateReceipt?: (transaction: PaymentTransaction, action: ReceiptAction) => void;
  /**
   * Whether this row already has a receipt operation in flight. Disables both menu items so a
   * second click cannot fire a duplicate request. Scoped per row: other rows stay usable.
   */
  isReceiptPending?: boolean;
}

/** One transaction: date, amount, account, payment type, and the Generate Receipt menu. */
export function TransactionRow({ transaction, onGenerateReceipt, isReceiptPending }: TransactionRowProps) {
  const { t } = useTranslation('members');
  const { formatCurrency } = useCurrency();
  const { formatDateOnly } = useFormatDate();

  const date = formatDateOnly(transaction.date);
  // Salesforce stores a payment as a negative amount, since it debits the member's balance. Product
  // asked for the magnitude on this table: the column is already understood as money paid, so a
  // minus sign reads as an error rather than as accounting. Applied at render time only, leaving the
  // API value untouched.
  const amount = formatCurrency(Math.abs(transaction.amount));

  return (
    <TableRow>
      <TableCell className="hidden w-40 whitespace-nowrap text-sm tabular-nums text-muted-foreground sm:table-cell">
        {date}
      </TableCell>
      <TableCell className="w-auto align-top sm:w-28 sm:align-middle">
        <div className="whitespace-nowrap text-sm font-semibold tabular-nums text-foreground">{amount}</div>
        {/* The date column is hidden on narrow viewports, so the date rides along under the amount. */}
        <div className="mt-1 text-xs tabular-nums text-muted-foreground sm:hidden">{date}</div>
      </TableCell>
      <TableCell className="align-top sm:w-64 sm:align-middle">
        <AccountCell account={transaction} />
      </TableCell>
      <TableCell className="w-auto align-top sm:w-36 sm:align-middle">
        <Badge variant="muted">{t(`payments.transactionTypes.${transaction.type}`)}</Badge>
      </TableCell>
      <TableCell className="w-12 text-right align-top sm:align-middle">
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('payments.transactions.receipt.triggerLabel', { amount, date })}
                  className="text-muted-foreground"
                />
              }
            >
              <Icon name="receipt" size="sm" />
            </DropdownMenuTrigger>
            {/*
             * The menu defaults to the trigger's width, which is a 32px icon button, so it collapses
             * to its min-width and wraps both labels. Sizing to content keeps each item on one line
             * and leaves room for longer translations.
             */}
            <DropdownMenuContent align="end" className="w-auto whitespace-nowrap">
              <DropdownMenuItem disabled={isReceiptPending} onClick={() => onGenerateReceipt?.(transaction, 'email')}>
                <Icon name="mail" size="xs" />
                {t('payments.transactions.receipt.email')}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isReceiptPending} onClick={() => onGenerateReceipt?.(transaction, 'pdf')}>
                <Icon name="fileArrowDown" size="xs" />
                {t('payments.transactions.receipt.pdf')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
